import React, { useState } from 'react';
import { BookOpen, Sparkles, TrendingUp, DollarSign, Package, ChevronDown, ChevronUp } from 'lucide-react';
import { getCompanyStory } from '../../../data/companyStories';
import { useScreenType } from '../../../hooks/useScreenType';

interface CompanyStoryCardProps {
    symbole?: string;
    nomSociete?: string;
    secteur?: string;
    style?: React.CSSProperties;
}

const CompanyStoryCard: React.FC<CompanyStoryCardProps> = ({
    symbole,
    nomSociete,
    secteur,
    style
}) => {
    const { isMobile } = useScreenType();
    const [isExpanded, setIsExpanded] = useState<boolean>(true);
    const story = getCompanyStory(symbole, nomSociete, secteur);

    return (
        <div
            className="company-story-card"
            style={{
                marginBottom: '20px',
                borderRadius: '16px',
                border: '1px solid var(--border-color)',
                background: 'linear-gradient(180deg, var(--bg-box) 0%, rgba(102, 126, 234, 0.03) 100%)',
                boxShadow: '0 4px 20px -2px rgba(0, 0, 0, 0.05)',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                ...style
            }}
        >
            {/* Header Strip */}
            <div
                onClick={() => setIsExpanded(!isExpanded)}
                style={{
                    padding: isMobile ? '12px 16px' : '16px 20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    background: 'rgba(102, 126, 234, 0.06)',
                    borderBottom: isExpanded ? '1px solid var(--border-color)' : 'none',
                    userSelect: 'none'
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '8px',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        boxShadow: '0 2px 6px rgba(102, 126, 234, 0.35)',
                        fontSize: '16px'
                    }}>
                        {story.emoji || <BookOpen size={16} />}
                    </div>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{
                                fontWeight: '700',
                                fontSize: isMobile ? '14px' : '15px',
                                color: 'var(--text-heading)',
                                letterSpacing: '-0.2px'
                            }}>
                                L'Essentiel en Clair
                            </span>
                        </div>
                        <div style={{
                            fontSize: isMobile ? '11px' : '12px',
                            color: 'var(--text-muted)',
                            fontStyle: 'italic',
                            marginTop: '2px'
                        }}>
                            « {story.slogan} »
                        </div>
                    </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{
                        fontSize: '11px',
                        color: '#667eea',
                        fontWeight: '600',
                        display: isMobile ? 'none' : 'inline'
                    }}>
                        {isExpanded ? 'Réduire' : 'Comprendre l’action'}
                    </span>
                    <button
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: 'var(--text-muted)',
                            padding: '4px',
                            display: 'flex',
                            alignItems: 'center'
                        }}
                        aria-label={isExpanded ? 'Réduire' : 'Agrandir'}
                    >
                        {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </button>
                </div>
            </div>

            {/* Content Body */}
            {isExpanded && (
                <div style={{ padding: isMobile ? '16px' : '20px 24px' }}>
                    {/* 3 Main Pillars Grid */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                        gap: isMobile ? '12px' : '16px',
                        marginBottom: '16px'
                    }}>
                        {/* 1. Ce qu'elle fait */}
                        <div style={{
                            padding: '14px 16px',
                            borderRadius: '12px',
                            background: 'var(--bg-body)',
                            border: '1px solid var(--border-color)',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between'
                        }}>
                            <div>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    marginBottom: '8px',
                                    fontSize: '11px',
                                    textTransform: 'uppercase',
                                    fontWeight: '700',
                                    letterSpacing: '0.5px',
                                    color: '#667eea'
                                }}>
                                    <Sparkles size={14} />
                                    <span>Ce qu'elle fait</span>
                                </div>
                                <p style={{
                                    margin: 0,
                                    fontSize: isMobile ? '12px' : '13px',
                                    lineHeight: '1.55',
                                    color: 'var(--text-main)',
                                    fontWeight: '400'
                                }}>
                                    {story.activite}
                                </p>
                            </div>
                        </div>

                        {/* 2. Modèle Économique */}
                        <div style={{
                            padding: '14px 16px',
                            borderRadius: '12px',
                            background: 'var(--bg-body)',
                            border: '1px solid var(--border-color)',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between'
                        }}>
                            <div>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    marginBottom: '8px',
                                    fontSize: '11px',
                                    textTransform: 'uppercase',
                                    fontWeight: '700',
                                    letterSpacing: '0.5px',
                                    color: '#10b981'
                                }}>
                                    <DollarSign size={14} />
                                    <span>Comment elle gagne du cash</span>
                                </div>
                                <p style={{
                                    margin: 0,
                                    fontSize: isMobile ? '12px' : '13px',
                                    lineHeight: '1.55',
                                    color: 'var(--text-main)',
                                    fontWeight: '400'
                                }}>
                                    {story.modeleEco}
                                </p>
                            </div>
                        </div>

                        {/* 3. Matière Première / Corrélation */}
                        <div style={{
                            padding: '14px 16px',
                            borderRadius: '12px',
                            background: 'var(--bg-body)',
                            border: '1px solid var(--border-color)',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between'
                        }}>
                            <div>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    marginBottom: '8px',
                                    fontSize: '11px',
                                    textTransform: 'uppercase',
                                    fontWeight: '700',
                                    letterSpacing: '0.5px',
                                    color: '#f59e0b'
                                }}>
                                    <TrendingUp size={14} />
                                    <span>Moteur & Corrélation</span>
                                </div>
                                <div style={{
                                    display: 'inline-block',
                                    fontSize: '11px',
                                    fontWeight: '700',
                                    color: '#d97706',
                                    background: 'rgba(245, 158, 11, 0.12)',
                                    padding: '2px 8px',
                                    borderRadius: '6px',
                                    marginBottom: '6px'
                                }}>
                                    🎯 {story.matierePremiere}
                                </div>
                                <p style={{
                                    margin: 0,
                                    fontSize: isMobile ? '12px' : '13px',
                                    lineHeight: '1.55',
                                    color: 'var(--text-main)',
                                    fontWeight: '400'
                                }}>
                                    {story.impactCorrelation}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Bottom Footnote: Daily Life Anchor */}
                    <div style={{
                        padding: isMobile ? '10px 12px' : '10px 16px',
                        borderRadius: '10px',
                        background: 'rgba(102, 126, 234, 0.05)',
                        border: '1px dashed rgba(102, 126, 234, 0.25)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: isMobile ? '11px' : '12px'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--text-heading)' }}>
                            <Package size={14} color="#667eea" style={{ flexShrink: 0 }} />
                            <span>
                                <b>Dans la vraie vie :</b> {story.produitQuotidien}
                            </span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CompanyStoryCard;
